import React, { useEffect, useState } from "react";
import { Calendar, Skeleton, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { listEntries } from "./api";

/**
 * Month view of one project's diary: filled days (locked days with a lock mark), missing
 * working days in red. `onSelect(date, entry|null)` opens a day. `onLoaded(result)` passes the
 * month's list up (for the list under the calendar). `reload` changes force a refetch.
 */
const DiaryCalendar = ({ project, onSelect, onLoaded, reload = 0 }) => {
  const { t } = useTranslation("dashboard");
  const [month, setMonth] = useState(() => dayjs().startOf("month"));
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!project) return undefined;
    let cancelled = false;
    const from = month.startOf("month").format("YYYY-MM-DD");
    const end = month.endOf("month");
    const to = (end.isAfter(dayjs()) ? dayjs() : end).format("YYYY-MM-DD");
    if (to < from) {
      setResult({ data: [], missing: [] });
      return undefined;
    }
    setResult(null);
    listEntries({ project, from, to })
      .then(res => {
        if (cancelled) return;
        setResult(res.data);
        onLoaded?.(res.data);
      })
      .catch(generalErrorAlert);
    return () => {
      cancelled = true;
    };
  }, [project, month.format("YYYY-MM"), reload]);

  const byDate = new Map((result?.data || []).map(entry => [entry.date, entry]));
  const missing = new Set(result?.missing || []);

  const labels = { filled: t("Entry"), locked: t("Locked"), missing: t("Missing") };

  const cell = (date, info) => {
    if (info.type !== "date") return info.originNode;
    const key = date.format("YYYY-MM-DD");
    const entry = byDate.get(key);
    const state = entry ? (entry.locked ? "locked" : "filled") : missing.has(key) ? "missing" : "";
    return (
      <div
        className={`ant-picker-cell-inner sdi-cal-cell${state ? ` sdi-cal-cell--${state}` : ""}`}
      >
        <span className="sdi-cal-day">{date.date()}</span>
        {state && <span className="sdi-cal-dot" aria-label={labels[state]} />}
      </div>
    );
  };

  return (
    <div className="sdi-calendar">
      {result ? null : <Skeleton.Input active block size="small" />}
      <Calendar
        fullscreen={false}
        value={month}
        disabledDate={date => date.isAfter(dayjs(), "day")}
        onPanelChange={value => setMonth(value.startOf("month"))}
        onSelect={(date, info) => {
          if (info?.source === "date" || !info) {
            if (!date.isSame(month, "month")) setMonth(date.startOf("month"));
            const key = date.format("YYYY-MM-DD");
            onSelect?.(key, byDate.get(key) || null);
          }
        }}
        fullCellRender={cell}
      />
      <Space size={16} wrap className="sdi-legend">
        <Typography.Text type="secondary">
          <span className="sdi-legend-dot sdi-legend-dot--filled" /> {t("Entry")}
        </Typography.Text>
        <Typography.Text type="secondary">
          <span className="sdi-legend-dot sdi-legend-dot--locked" /> {t("Locked")}
        </Typography.Text>
        <Typography.Text type="secondary">
          <span className="sdi-legend-dot sdi-legend-dot--missing" /> {t("Missing")}
        </Typography.Text>
      </Space>
    </div>
  );
};

export default DiaryCalendar;
